/**
 * DEFCAT P6 — a single-axis editable treeview panel (used twice: Location +
 * Type). Expand/collapse, select a node, and "add child" / "add root".
 * Rename / retire / move are driven from the detail panel in the editor.
 *
 * DEFCAT-R2-09 (drag upgrade, 2026-06-04) — sibling reordering is mouse
 * drag-and-drop via @dnd-kit. Each draggable row shows a DragIndicator grab
 * handle (where the old ▲▼ buttons sat); the user grabs it and drops the node
 * to a new position WITHIN its sibling group. One SortableContext per sibling
 * group scopes the drag to the group; cross-group drops are rejected. The
 * KeyboardSensor gives keyboard DnD (space + arrows) on the handle for a11y.
 *
 * Optionally renders a leading checkbox per node (Dependencies overlay editor)
 * with a faded "inherited" state.
 */
import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import type { TreeNode } from "./treeBuild.js";
import { locText } from "./locText.js";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  GripVerticalIcon,
  PlusIcon,
} from "@aeriosiq/ui/icons";

/** A node is an L0 reference root (read-only) when its TreeNode carries `l0`. */
function isL0(node: TreeNode): boolean {
  return node.l0 != null;
}

/**
 * DEFCAT-R2-09 — a single reorder step WITHIN a sibling group. `siblingIds` is
 * the ordered list of entryIds sharing the moved node's parent; `from`/`to`
 * are indices into it. Cross-parent moves are structurally impossible here
 * (re-parenting stays in the detail panel's Parent dropdown).
 */
export interface ReorderEvent {
  parentId: string | null;
  siblingIds: string[];
  from: number;
  to: number;
}

/**
 * DEFCAT-R2-09 (drag) — pure drop resolver. Given the dragged node id, the drop
 * target id, and the ORDERED list of draggable sibling nodes (one group), emit
 * the ReorderEvent for the within-group move, or `null` when the move is a
 * no-op or invalid (drop onto self, target not in the group → cross-group drag
 * is rejected). The L0 reference roots are never draggable and never in
 * `siblings`, so a drop targeting one resolves to `null` here too.
 */
export function onSiblingDragEnd(
  activeId: string,
  overId: string | null,
  siblings: TreeNode[],
): ReorderEvent | null {
  if (overId === null || activeId === overId) return null;
  const ids = siblings.map((s) => s.entry.entryId);
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  // Both ends must live in the SAME sibling group (cross-group → reject).
  if (from < 0 || to < 0) return null;
  // Parent of this group: every draggable sibling shares it.
  const parentId = siblings[0]?.entry.parentEntryId ?? null;
  return { parentId, siblingIds: ids, from, to };
}

export interface TreePanelProps {
  title: string;
  nodes: TreeNode[];
  selectedId: string | null;
  onSelect: (entryId: string) => void;
  /** "add child" target — when set, a button appears to add under the node. */
  onAddChild?: ((parentId: string | null) => void) | undefined;
  addRootLabel?: string;
  /**
   * DEFCAT-R2-11 — level-aware add-button text. Given the parent node (null at
   * the root), return the "+ Add <label>" caption. Overrides `addRootLabel`
   * for the root button and labels every per-row add icon's aria-label.
   */
  addLabelFor?: ((parent: TreeNode | null) => string) | undefined;
  /** Overlay-editor mode: render a checkbox per node. */
  checkable?: boolean;
  checkedIds?: ReadonlySet<string>;
  inheritedIds?: ReadonlySet<string>;
  /** DEFCAT-R2-12 — node-local EXCLUSION ids (struck/badge state). */
  excludedIds?: ReadonlySet<string>;
  onToggleCheck?: ((entryId: string, checked: boolean) => void) | undefined;
  /**
   * DEFCAT-R2-09 — when set (draft versions only), each draggable row gets a
   * DragIndicator grab handle that reorders it within its sibling group via
   * drag-and-drop (mouse + keyboard). The handler persists the new order via
   * PATCH sortOrder. Absent on published versions ⇒ no handles, no DnD.
   */
  onReorder?: ((e: ReorderEvent) => void) | undefined;
}

interface RowProps {
  node: TreeNode;
  depth: number;
  props: TreePanelProps;
  expanded: Set<string>;
  toggleExpand: (id: string) => void;
  orderOverride: ReadonlyMap<string, string[]>;
}

function TreeRow({
  node,
  depth,
  props,
  expanded,
  toggleExpand,
  orderOverride,
}: RowProps): ReactElement {
  const { entry, children } = node;
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(entry.entryId);
  const selected = props.selectedId === entry.entryId;
  // DEFCAT-R2-04 — L0 reference roots are read-only: never struck/retirable,
  // no reorder, no add-child icon, not checkable in the overlay editor.
  const l0 = isL0(node);
  // Draggable iff the panel is in edit mode (onReorder set) AND this is a real
  // (non-L0) node. Retired nodes stay draggable within their group.
  const draggable = props.onReorder != null && !l0;
  const retired = !l0 && entry.isActive === false;
  const excluded = props.excludedIds?.has(entry.entryId) ?? false;
  const checked = !excluded && (props.checkedIds?.has(entry.entryId) ?? false);
  const inherited =
    !checked && !excluded && (props.inheritedIds?.has(entry.entryId) ?? false);

  // @dnd-kit sortable wiring. For non-draggable rows (L0 roots / published) we
  // still call the hook (rules of hooks) but never render the handle, and the
  // L0 id is excluded from the SortableContext items below so it's not a valid
  // sort target.
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    // animateLayoutChanges OFF: when the post-drop refetch lands (~250ms
    // later), dnd-kit would re-animate every sibling from stale measured
    // rects — rows visibly overlap and re-shuffle even though the order is
    // already correct (frame-by-frame video analysis, 2026-06-04). The
    // nice slide DURING the drag is transform-based and unaffected.
  } = useSortable({
    id: entry.entryId,
    disabled: !draggable,
    animateLayoutChanges: () => false,
  });

  return (
    <Fragment>
      <Box
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          pl: depth * 2,
          py: 0.25,
          borderRadius: 1,
          // Lift the dragged row above its siblings + dim it slightly.
          opacity: isDragging ? 0.85 : 1,
          position: "relative",
          zIndex: isDragging ? 1 : "auto",
          boxShadow: isDragging ? 3 : "none",
          bgcolor: isDragging
            ? "background.paper"
            : selected
              ? "action.selected"
              : "transparent",
          "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            aria-label={isOpen ? "Collapse" : "Expand"}
            onClick={() => {
              toggleExpand(entry.entryId);
            }}
          >
            {isOpen ? (
              <ChevronDownIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}

        {props.checkable && !l0 ? (
          <Checkbox
            size="small"
            // Inherited ticks render checked-but-faded and stay tickable:
            // unticking one writes an EXCLUSION (R2-12). Excluded nodes show
            // unchecked (the struck label conveys the exclusion).
            checked={checked || inherited}
            indeterminate={inherited}
            onChange={(e) => {
              props.onToggleCheck?.(entry.entryId, e.target.checked);
            }}
            inputProps={{
              "aria-label": `Valid: ${locText(entry.name)}`,
            }}
            {...(inherited ? { sx: { opacity: 0.5 } } : {})}
          />
        ) : props.checkable ? (
          // L0 placeholder keeps the rows aligned without a tick.
          <Box sx={{ width: 38, flexShrink: 0 }} />
        ) : null}

        <Box
          component="button"
          type="button"
          onClick={() => {
            props.onSelect(entry.entryId);
          }}
          sx={{
            flex: 1,
            textAlign: "left",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 14,
            color:
              retired || excluded
                ? "text.disabled"
                : l0
                  ? "text.secondary"
                  : "text.primary",
            fontStyle: l0 ? "italic" : "normal",
            textDecoration: retired || excluded ? "line-through" : "none",
            py: 0.5,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {locText(entry.name)}
        </Box>

        {draggable ? (
          // The drag handle — sits exactly where the ▲▼ buttons used to. Drag
          // activates ONLY from here (listeners on the handle, not the row), so
          // click-to-select + expand/collapse keep working. KeyboardSensor adds
          // space-to-grab + arrows-to-move for a11y.
          <IconButton
            ref={setActivatorNodeRef}
            size="small"
            aria-label={`Reorder ${locText(entry.name)}`}
            disableRipple
            sx={{
              flexShrink: 0,
              cursor: "grab",
              touchAction: "none",
              "&:active": { cursor: "grabbing" },
            }}
            {...attributes}
            {...listeners}
          >
            <GripVerticalIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}

        {props.onAddChild ? (
          <IconButton
            size="small"
            aria-label={
              props.addLabelFor
                ? `${props.addLabelFor(node)} under ${locText(entry.name)}`
                : `Add child under ${locText(entry.name)}`
            }
            onClick={() => {
              props.onAddChild?.(entry.entryId);
            }}
          >
            <PlusIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
      {hasChildren && isOpen ? (
        <SiblingGroup
          siblings={children}
          depth={depth + 1}
          props={props}
          expanded={expanded}
          toggleExpand={toggleExpand}
          orderOverride={orderOverride}
        />
      ) : null}
    </Fragment>
  );
}

/**
 * Membership key for a sibling group — stable across REORDERS (sorted ids),
 * changes when membership changes. Used by the local order override below.
 */
export function groupKey(ids: readonly string[]): string {
  return [...ids].sort().join("|");
}

/** Apply a local order override to a sibling list (same membership only). */
function applyOverride(
  siblings: TreeNode[],
  override: ReadonlyMap<string, string[]>,
): TreeNode[] {
  const draggableIds = siblings
    .filter((n) => n.l0 == null)
    .map((n) => n.entry.entryId);
  const order = override.get(groupKey(draggableIds));
  if (!order) return siblings;
  const pos = new Map(order.map((id, i) => [id, i]));
  return [...siblings].sort((a, b) => {
    const pa = pos.get(a.entry.entryId);
    const pb = pos.get(b.entry.entryId);
    if (pa === undefined || pb === undefined) return 0; // L0 etc. keep place
    return pa - pb;
  });
}

interface SiblingGroupProps {
  siblings: TreeNode[];
  depth: number;
  props: TreePanelProps;
  expanded: Set<string>;
  toggleExpand: (id: string) => void;
  orderOverride: ReadonlyMap<string, string[]>;
}

/**
 * One SortableContext per sibling group — this naturally scopes dragging to the
 * group (verticalListSortingStrategy). Only real (non-L0) nodes are sortable
 * items, so an L0 reference root is never a valid drop target for a cross-group
 * move. When the panel isn't editable (no onReorder) the rows are plain.
 */
function SiblingGroup({
  siblings,
  depth,
  props,
  expanded,
  toggleExpand,
  orderOverride,
}: SiblingGroupProps): ReactElement {
  // Local order override (set in the SAME event tick as drag-end) wins until
  // the props catch up — guarantees the post-drop order renders in the same
  // React commit that clears the drag transforms.
  const ordered = useMemo(
    () => applyOverride(siblings, orderOverride),
    [siblings, orderOverride],
  );
  const items = useMemo(
    () => ordered.filter((n) => n.l0 == null).map((n) => n.entry.entryId),
    [ordered],
  );
  const rows = ordered.map((c) => (
    <TreeRow
      key={c.entry.entryId}
      node={c}
      depth={depth}
      props={props}
      expanded={expanded}
      toggleExpand={toggleExpand}
      orderOverride={orderOverride}
    />
  ));
  if (props.onReorder == null) return <>{rows}</>;
  return (
    <SortableContext items={items} strategy={verticalListSortingStrategy}>
      {rows}
    </SortableContext>
  );
}

export function TreePanel(props: TreePanelProps): ReactElement {
  // DEFCAT-R2-04 — L0 reference roots start EXPANDED so their location sub-trees
  // are visible without a click (directory-tree ergonomics; the L0 layer is
  // context, not something to keep collapsed). Other nodes start collapsed.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const init = new Set<string>();
    for (const n of props.nodes) if (n.l0 != null) init.add(n.entry.entryId);
    return init;
  });
  function toggleExpand(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // PointerSensor with a small activation distance so a plain click on the
  // handle still selects/expands without starting a phantom drag; KeyboardSensor
  // drives accessible reordering (space to grab, arrows to move, space to drop).
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Index of sibling groups by their member ids → the ordered node list. The
  // drag-end handler looks up the active node's group and rejects any drop
  // whose `over` is not in the same group (cross-group drag).
  const groups = useMemo(
    () => collectSiblingGroups(props.nodes),
    [props.nodes],
  );

  // Local order override per sibling group (keyed by MEMBERSHIP, stable
  // across reorders). Set in the SAME event tick as drag-end so the new order
  // commits together with dnd-kit clearing its transforms — without it there
  // is a ~1-2 frame flash of the OLD order between drop and the optimistic
  // cache render (component setState batches with dnd-kit's; an external
  // store write does not, frame-by-frame video analysis 2026-06-04).
  const [orderOverride, setOrderOverride] = useState<Map<string, string[]>>(
    () => new Map(),
  );

  // Drop stale overrides once the props-derived order has caught up (or the
  // membership changed) so external updates are never fought.
  useEffect(() => {
    if (orderOverride.size === 0) return;
    const natural = new Map<string, string[]>();
    function visit(siblings: TreeNode[]): void {
      const ids = siblings
        .filter((n) => n.l0 == null)
        .map((n) => n.entry.entryId);
      if (ids.length > 0) natural.set(groupKey(ids), ids);
      for (const n of siblings) if (n.children.length > 0) visit(n.children);
    }
    visit(props.nodes);
    const next = new Map(orderOverride);
    let changed = false;
    for (const [key, order] of orderOverride) {
      const nat = natural.get(key);
      if (!nat || nat.join("|") === order.join("|")) {
        next.delete(key);
        changed = true;
      }
    }
    if (changed) setOrderOverride(next);
  }, [props.nodes, orderOverride]);

  function handleDragEnd(event: DragEndEvent): void {
    if (props.onReorder == null) return;
    const activeId = String(event.active.id);
    const overId = event.over != null ? String(event.over.id) : null;
    const group = groups.get(activeId);
    if (!group) return;
    const ev = onSiblingDragEnd(activeId, overId, group);
    if (ev) {
      // Same-tick local reorder (batches with dnd-kit's drag-end setState).
      const newOrder = arrayMove(ev.siblingIds, ev.from, ev.to);
      setOrderOverride((prev) =>
        new Map(prev).set(groupKey(ev.siblingIds), newOrder),
      );
      props.onReorder(ev);
    }
  }

  return (
    <Box
      sx={{
        // R2-05: stretch to the full width of the outer widget so the inner
        // scroll container's scrollbar lands at the widget's right edge rather
        // than inset mid-widget. The parent Paper uses display:flex, so without
        // an explicit width the panel only takes its content width.
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2">{props.title}</Typography>
        {props.onAddChild ? (
          <Button
            size="small"
            startIcon={<PlusIcon />}
            onClick={() => {
              props.onAddChild?.(null);
            }}
          >
            {props.addLabelFor?.(null) ?? props.addRootLabel ?? "Add"}
          </Button>
        ) : null}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {props.nodes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No entries yet.
          </Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            // DEFCAT (Christian 2026-06-04) — clamp the drag to the vertical
            // axis so a dragged row can't slide sideways and expand the widget's
            // inner canvas (horizontal scroll). `restrictToFirstScrollableAncestor`
            // additionally keeps the row from escaping the scroll container above
            // or below the list.
            modifiers={[
              restrictToVerticalAxis,
              restrictToFirstScrollableAncestor,
            ]}
          >
            <SiblingGroup
              siblings={props.nodes}
              depth={0}
              props={props}
              expanded={expanded}
              toggleExpand={toggleExpand}
              orderOverride={orderOverride}
            />
          </DndContext>
        )}
      </Box>
    </Box>
  );
}

/**
 * Walk the tree, mapping every real (non-L0) node id → the ordered list of its
 * sibling nodes (the group it belongs to). The drag-end handler uses this to
 * resolve a drop to a within-group ReorderEvent and to reject cross-group drops.
 */
function collectSiblingGroups(nodes: TreeNode[]): Map<string, TreeNode[]> {
  const map = new Map<string, TreeNode[]>();
  function visit(siblings: TreeNode[]): void {
    for (const n of siblings) {
      if (n.l0 == null) map.set(n.entry.entryId, siblings);
      if (n.children.length > 0) visit(n.children);
    }
  }
  visit(nodes);
  return map;
}
